import { Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { TableSkeleton } from "@/components/loading";
import { useTlAtRiskStudents } from "@/hooks/use-tl-exams";
import type { TlAtRiskStudent } from "@/lib/api/tl-exams";
import { useTLGradingQueue } from "@/hooks/use-tl-dashboard";
import { useStaffTickets } from "@/hooks/use-tickets";
import { TicketStatus, TicketPriority, type TicketListItem } from "@/lib/api/tickets";
import type { GradingQueueItem } from "@/lib/types";
import { bidiText } from "@/lib/utils";
import { AlertTriangle, ClipboardCheck, LifeBuoy, ShieldCheck, ChevronRight } from "lucide-react";

export type AttentionFocusTab = "at-risk" | "grading" | "tickets";

interface AttentionPanelProps {
  focus: AttentionFocusTab;
  onFocusChange: (focus: AttentionFocusTab) => void;
}

interface AtRiskTabContentProps {
  isLoading: boolean;
  isError: boolean;
  students: TlAtRiskStudent[];
  onRefetch: () => void;
}

function AtRiskTabContent({ isLoading, isError, students, onRefetch }: AtRiskTabContentProps) {
  if (isLoading) {
    return (
      <div className="py-2">
        <TableSkeleton rows={4} columns={3} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm font-medium">Could not load at-risk students.</span>
        </div>
        <Button variant="outline" size="sm" onClick={onRefetch}>
          Try again
        </Button>
      </div>
    );
  }

  if (students.length === 0) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="No students flagged at risk"
        description="No students currently meet the at-risk criteria."
        className="py-8"
      />
    );
  }

  return (
    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
      {students.map((student) => {
        const isHighRisk = student.riskScore >= 3;
        const isMediumRisk = student.riskScore === 2;
        const isLowRisk = student.riskScore === 1;

        return (
          <Link
            key={student.studentId}
            to="/team-leader/exams/student/$studentId"
            params={{ studentId: student.studentId }}
            className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground truncate" {...bidiText}>
                  {student.studentName}
                </p>
                <Badge
                  variant={isHighRisk ? "destructive" : isLowRisk ? "secondary" : "outline"}
                  className={isMediumRisk ? "bg-warning-muted text-warning border-warning/30" : ""}
                >
                  Risk {student.riskScore}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                <span {...bidiText}>{student.groupName || "Unassigned"}</span> · {student.email}
              </p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {student.failingGradesFlag && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 text-muted-foreground"
                  >
                    Failing Grades
                  </Badge>
                )}
                {student.lowActivityFlag && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 text-muted-foreground"
                  >
                    Low Activity
                  </Badge>
                )}
                {student.checkpointRiskFlag && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 text-muted-foreground"
                  >
                    Checkpoint Risk
                  </Badge>
                )}
                {student.violationRiskFlag && (
                  <Badge
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 text-muted-foreground"
                  >
                    Violations
                  </Badge>
                )}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          </Link>
        );
      })}
    </div>
  );
}

interface GradingTabContentProps {
  isLoading: boolean;
  isError: boolean;
  items: GradingQueueItem[];
  onRefetch: () => void;
}

function GradingTabContent({ isLoading, isError, items, onRefetch }: GradingTabContentProps) {
  if (isLoading) {
    return (
      <div className="py-2">
        <TableSkeleton rows={4} columns={3} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm font-medium">Could not load grading queue.</span>
        </div>
        <Button variant="outline" size="sm" onClick={onRefetch}>
          Try again
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={ClipboardCheck}
        title="No submissions awaiting grading"
        description="All student submissions have been graded."
        className="py-8"
      />
    );
  }

  return (
    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
      {items.map((item) => (
        <Link
          key={item.sessionId}
          to="/team-leader/exams/review"
          className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground truncate" {...bidiText}>
                {item.studentName || item.studentEmail}
              </p>
              <Badge variant="secondary" className="text-xs">
                {item.pendingCount} pending
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5" {...bidiText}>
              {item.examTitle}
            </p>
            {item.submittedAt && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Submitted{" "}
                {new Date(item.submittedAt).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            )}
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

interface TicketsTabContentProps {
  isLoading: boolean;
  isError: boolean;
  tickets: TicketListItem[];
  onRefetch: () => void;
}

function TicketsTabContent({ isLoading, isError, tickets, onRefetch }: TicketsTabContentProps) {
  if (isLoading) {
    return (
      <div className="py-2">
        <TableSkeleton rows={4} columns={3} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm font-medium">Could not load support tickets.</span>
        </div>
        <Button variant="outline" size="sm" onClick={onRefetch}>
          Try again
        </Button>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <EmptyState
        icon={LifeBuoy}
        title="No open support tickets"
        description="There are currently no tickets requiring attention."
        className="py-8"
      />
    );
  }

  return (
    <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
      {tickets.map((ticket) => {
        const isCriticalOrEscalated =
          ticket.priority === TicketPriority.Critical ||
          ticket.priority === TicketPriority.Escalated;
        const isHighPriority = ticket.priority === TicketPriority.High;
        const isBreached = Boolean(ticket.firstResponseBreachedAt || ticket.resolutionBreachedAt);

        return (
          <Link
            key={ticket.id}
            to="/team-leader/tickets"
            className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-foreground truncate" {...bidiText}>
                  {ticket.subject}
                </p>
                <Badge
                  variant={
                    isCriticalOrEscalated ? "destructive" : isHighPriority ? "outline" : "secondary"
                  }
                  className={
                    isHighPriority ? "bg-warning-muted text-warning border-warning/30" : ""
                  }
                >
                  {ticket.priority === TicketPriority.Escalated
                    ? "Escalated"
                    : ticket.priority === TicketPriority.Critical
                      ? "Critical"
                      : ticket.priority === TicketPriority.High
                        ? "High"
                        : ticket.priority === TicketPriority.Normal
                          ? "Normal"
                          : "Low"}
                </Badge>
                {isBreached && (
                  <Badge variant="destructive" className="text-[10px] gap-1 px-1.5 py-0">
                    <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                    SLA Breached
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                <span {...bidiText}>
                  {ticket.studentFullName || ticket.studentEmail || "Student"}
                </span>{" "}
                · {ticket.topicTitle}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          </Link>
        );
      })}
    </div>
  );
}

export function AttentionPanel({ focus, onFocusChange }: AttentionPanelProps) {
  // ── 1. At Risk Query ──────────────────────────────────────────────────────
  const {
    data: atRiskData,
    isLoading: atRiskLoading,
    isError: atRiskError,
    refetch: refetchAtRisk,
  } = useTlAtRiskStudents({ minRiskFlags: 1, pageSize: 8 });

  // ── 2. Grading Queue Query ────────────────────────────────────────────────
  const {
    data: gradingQueue = [],
    isLoading: gradingLoading,
    isError: gradingError,
    refetch: refetchGrading,
  } = useTLGradingQueue();

  // ── 3. Open Tickets Query ─────────────────────────────────────────────────
  const {
    data: ticketsData,
    isLoading: ticketsLoading,
    isError: ticketsError,
    refetch: refetchTickets,
  } = useStaffTickets({ status: TicketStatus.Open, pageSize: 8 });

  const atRiskCount = atRiskData?.totalCount;
  const gradingCount = gradingQueue.length;
  const ticketsCount = ticketsData?.totalCount;

  const atRiskStudents = atRiskData?.items ?? [];
  const tickets = ticketsData?.items ?? [];

  return (
    <Card className="lg:col-span-2">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
          Needs Your Attention
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={focus} onValueChange={(val) => onFocusChange(val as AttentionFocusTab)}>
          <TabsList className="w-full justify-start mb-4">
            <TabsTrigger value="at-risk" className="flex items-center gap-1.5">
              <span>At Risk</span>
              {!atRiskLoading && !atRiskError && atRiskCount !== undefined && (
                <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-semibold">
                  {atRiskCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="grading" className="flex items-center gap-1.5">
              <span>To Grade</span>
              {!gradingLoading && !gradingError && (
                <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-semibold">
                  {gradingCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tickets" className="flex items-center gap-1.5">
              <span>Tickets</span>
              {!ticketsLoading && !ticketsError && ticketsCount !== undefined && (
                <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-semibold">
                  {ticketsCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── At Risk Tab Content ───────────────────────────────────────── */}
          <TabsContent value="at-risk" className="mt-0">
            <AtRiskTabContent
              isLoading={atRiskLoading}
              isError={atRiskError}
              students={atRiskStudents}
              onRefetch={() => refetchAtRisk()}
            />
          </TabsContent>

          {/* ── To Grade Tab Content ──────────────────────────────────────── */}
          <TabsContent value="grading" className="mt-0">
            <GradingTabContent
              isLoading={gradingLoading}
              isError={gradingError}
              items={gradingQueue}
              onRefetch={() => refetchGrading()}
            />
          </TabsContent>

          {/* ── Tickets Tab Content ───────────────────────────────────────── */}
          <TabsContent value="tickets" className="mt-0">
            <TicketsTabContent
              isLoading={ticketsLoading}
              isError={ticketsError}
              tickets={tickets}
              onRefetch={() => refetchTickets()}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
